import styled, { css } from 'styled-components';
import { AxiosError } from 'axios';
import {
    AdditionalCostCorrectionDetailsDto,
    AdditionalCostDetailsDto,
    ChildId,
} from '../../../../../../types';
import { AdditionalCostCorrectionItem } from '../AdditionalCostCorrectionItem/AdditionalCostCorrectionItem';
import { AdditionalCostItem } from '../AdditionalCostItem/AdditionalCostItem';
import { AppButton } from '../../../../../atoms';
import { If } from '../../../../../atoms/If/If';
import { commonTexts, settlementTexts } from '../../../../../../consts/text';
import { useAppMutation } from '../../../../../../services/reactQuery/useAppMutation';
import StaffHTTPService from '../../../../../../services/HTTPService/staff/StaffHTTPService';
import { useSimpleNotification } from '../../../../../../hooks/useSimpleNotification/useSimpleNotification';
import { settlementsVerifyErrorParser } from '../../../../../../utils/errorHandlers/settlement/settlementsVerifyErrorParser';
import { useToggle } from '../../../../../../hooks/useToggle/useToggle';
import { AppConfirmModal } from '../../../../../organisms';
import { UserDollar } from '../../../../../../assets';

export const BillDetailsContainer = styled.div`
    ${({ theme }) => css`
        padding: 16px;
        display: grid;
        grid-template-columns: 1fr;
        gap: 1rem;
        border: 1px solid ${theme.colors.primary.primary5};
        border-radius: 10px;
    `}
`;

export const Header = styled('div')`
    display: flex;
    justify-content: flex-end;
    align-items: center;
`;

type Props = {
    detailsDto: AdditionalCostDetailsDto;
    corrections: Array<AdditionalCostCorrectionDetailsDto>;
    childId: ChildId;
    index: number;
};

export const AdditionalCostPackage = ({ detailsDto, corrections, childId, index }: Props) => {
    const { displaySuccess, displayError } = useSimpleNotification();

    const [verifyCostModalOpen, openVerifyCostModal, closeVerifyCostModal] = useToggle(false);
    const [
        verifyCostCorrectionModalOpen,
        openVerifyCostCorrectionModal,
        closeVerifyCostCorrectionModal,
    ] = useToggle(false);

    const { mutate: verifyAdditionalCost, isLoading: verifyAdditionalCostIsLoading } =
        useAppMutation(StaffHTTPService.settlements.verifyAdditionalCost, {
            key: ['VERIFY_MANUAL_BILL'],
            onSuccess: () => {
                displaySuccess(settlementTexts.children.successfullyVerifyAdditionalCostMessage);
            },
            onError: () => {
                displayError(settlementTexts.children.errorVerifyAdditionalCostMessage);
            },
            invalidateQueryKeys: [
                ['ADDITIONAL_COST_DETAILS'],
                ['ADDITIONAL_COST_LIST'],
                ['ADDITIONAL_COST'],
                ['CHILD_SETTLEMENT_BALANCE'],
            ],
        });

    const { mutate: addAdditionalCostCorrection, isLoading: addAdditionalCostCorrectionIsLoading } =
        useAppMutation(StaffHTTPService.settlements.addAdditionalCostCorrection, {
            key: ['ADD_ADDITIONAL_COST_CORRECTION'],
            onSuccess: () => {
                displaySuccess(
                    settlementTexts.children.successfullyAddAdditionalCostCorrectionMessage,
                );
            },
            onError: () => {
                displayError(settlementTexts.children.errorAddAdditionalCostCorrectionMessage);
            },
            invalidateQueryKeys: [
                ['ADDITIONAL_COST_DETAILS'],
                ['ADDITIONAL_COST_LIST'],
                ['ADDITIONAL_COST'],
                ['CHILD_SETTLEMENT_BALANCE'],
            ],
        });

    const { mutate: verifyChildCorrection, isLoading: isVerifyChildCorrectionLoading } =
        useAppMutation(StaffHTTPService.settlements.verifyChildCorrection, {
            key: ['VERIFY_CHILD_CORRECTION'],
            onSuccess: () => {
                // closeVerifyModal();
                displaySuccess(
                    settlementTexts.children.verifySettlements.singleSettlement
                        .verifySettlementSuccessMessage,
                );
            },
            onError: (error: AxiosError) =>
                displayError(
                    settlementsVerifyErrorParser(error, commonTexts.errorMessages.actionExecution),
                ),
            invalidateQueryKeys: [
                ['ADDITIONAL_COST_DETAILS'],
                ['ADDITIONAL_COST_LIST'],
                ['ADDITIONAL_COST'],
                ['CHILD_SETTLEMENT_BALANCE'],
            ],
        });

    const handleVerify = (id?: string) => {
        if (id) {
            verifyAdditionalCost({ additionalCostId: id });
        }
    };

    const handleAddCorrection = (id?: string) => {
        if (id) {
            addAdditionalCostCorrection({ additionalCostId: id });
        }
    };

    const handleVerifyCorrection = (id?: string) => {
        if (id) {
            verifyChildCorrection(id);
        }
    };

    const isMainCostVerified = detailsDto.verified;
    const areCorrectionsPresent = !!corrections?.length;
    const isLastCorrectionVerified = !!corrections?.at(0)?.verified;
    const lastCorrectionId = corrections?.at(0)?.id;

    return (
        <>
            <BillDetailsContainer>
                <Header>
                    <If condition={!areCorrectionsPresent}>
                        <If condition={!isMainCostVerified}>
                            <AppButton
                                onClick={openVerifyCostModal}
                                type="primary"
                                margin={0}
                                loading={verifyAdditionalCostIsLoading}
                            >
                                {
                                    settlementTexts.children.manualBill.settlementDetails
                                        .createInvoice
                                }
                            </AppButton>
                        </If>
                        <If condition={isMainCostVerified}>
                            <AppButton
                                onClick={() => handleAddCorrection(detailsDto.id)}
                                margin={0}
                                loading={addAdditionalCostCorrectionIsLoading}
                            >
                                {
                                    settlementTexts.children.manualBill.settlementDetails
                                        .addCorrection
                                }
                            </AppButton>
                        </If>
                    </If>
                    <If condition={areCorrectionsPresent}>
                        <If condition={!isLastCorrectionVerified}>
                            <AppButton
                                onClick={openVerifyCostCorrectionModal}
                                type="primary"
                                margin={0}
                                loading={isVerifyChildCorrectionLoading}
                            >
                                {
                                    settlementTexts.children.manualBill.settlementDetails
                                        .createInvoiceCorrection
                                }
                            </AppButton>
                        </If>
                        <If condition={isLastCorrectionVerified}>
                            <AppButton
                                onClick={() => handleAddCorrection(detailsDto.id)}
                                margin={0}
                                loading={addAdditionalCostCorrectionIsLoading}
                            >
                                {
                                    settlementTexts.children.manualBill.settlementDetails
                                        .addCorrection
                                }
                            </AppButton>
                        </If>
                    </If>
                </Header>

                {corrections?.map((correction) => {
                    return (
                        <AdditionalCostCorrectionItem
                            correction={correction}
                            detailsDto={detailsDto}
                            childId={childId}
                            index={index}
                        />
                    );
                })}

                <AdditionalCostItem detailsDto={detailsDto} childId={childId} index={index} />
            </BillDetailsContainer>
            <AppConfirmModal
                open={verifyCostModalOpen}
                width={420}
                title="Czy na pewno chcesz wystawić rozliczenie dla kosztu dodatkowego?"
                icon={<UserDollar />}
                confirmActionLoading={verifyAdditionalCostIsLoading}
                okButtonProps={{ title: 'Wystaw rozliczenie', danger: true }}
                cancelButtonProps={{ title: 'Anuluj' }}
                onOk={() => handleVerify(detailsDto.id)}
                onCancel={closeVerifyCostModal}
            />
            <AppConfirmModal
                open={verifyCostCorrectionModalOpen}
                width={420}
                title="Czy na pewno chcesz wystawić korektę kosztu dodatkowego?"
                icon={<UserDollar />}
                okButtonProps={{ title: 'Wystaw korektę', danger: true }}
                cancelButtonProps={{ title: 'Anuluj' }}
                confirmActionLoading={isVerifyChildCorrectionLoading}
                onOk={() => handleVerifyCorrection(lastCorrectionId)}
                onCancel={closeVerifyCostCorrectionModal}
            />
        </>
    );
};
